/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 201906029 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { BillOfQuantitiesPositionsDaoService } from '../../dao/bill-of-quantities-positions-dao.service';
import { BillOfQuantitiesPositionsService } from '../bill-of-quantities-positions.service';
import { BillOfQuantitiesPosition, BillOfQuantitiesPositionDTO } from 'src/app/_model/bill-of-quantities-positions';
import { IMSId } from 'src/app/_model/ims-object';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';

/**
 * Loads BillOfQuantities
 */
@Injectable()
export class BillOfQuantitiesPositionsLogicService implements BillOfQuantitiesPositionsService {
  public billOfQuantitiesPositions$ = new BehaviorSubject<BillOfQuantitiesPosition[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> = new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private billOfQuantitiesPositionsDaoService: BillOfQuantitiesPositionsDaoService,
  ) {
    this.initialize();
  }

  public async updateBillOfQuantitiesPositions(billOfQuantitiesPositionsDto: BillOfQuantitiesPositionDTO[]): Promise<void> {
    this.logger.log(
      this.constructor.name,
      '- Received new bill of quantities positions from other IMS component',
      billOfQuantitiesPositionsDto
    );

    try {
      let billOfQuantitiesPositions: BillOfQuantitiesPosition[] = [];

      for (const billOfQuantitiesPositionDto of billOfQuantitiesPositionsDto) {
        billOfQuantitiesPositions.push(BillOfQuantitiesPosition.fromDto(billOfQuantitiesPositionDto));
      }

      await this.billOfQuantitiesPositionsDaoService.saveBillOfQuantitiesPositions(billOfQuantitiesPositions);
      await this.billOfQuantitiesPositions$.next(billOfQuantitiesPositions);

    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error parsing BillOfQuantitiesPositions: ',
        error?.message
      );
      throw error;
    }
  }

  
  public getBillOfQuantitiesPositionsForBillOfQuantityId(id: IMSId): BillOfQuantitiesPosition[] {
    if (id === undefined) {
      return undefined;
    }

    return this.billOfQuantitiesPositions$.getValue().filter((boqp) => {
      if (boqp.billOfQuantity.getImsId() === id.imsId) {
        return boqp;
      }
      return undefined;
    });
  }

  // Resetable
  public async reset() {
    await this.billOfQuantitiesPositionsDaoService.reset();
  }

  public async clear() {
    await this.billOfQuantitiesPositionsDaoService.clear();
    this.billOfQuantitiesPositions$.next([]);
  }


  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING)
    try {
      await this.loadLocalBillOfQuantitiesPositions();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED)
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE)
    }
  }

  // Private Functions
  private async loadLocalBillOfQuantitiesPositions() {
    try {
      this.logger.log(this.constructor.name, '- Load local BillOfQuantitiesPositions');
      let billOfQuantitiesPositions: BillOfQuantitiesPosition[] = await this.billOfQuantitiesPositionsDaoService.getBillOfQuantitiesPositions();
      this.logger.log(this.constructor.name, '- Loaded local BillOfQuantitiesPositions:', billOfQuantitiesPositions.length);

      this.billOfQuantitiesPositions$.next(billOfQuantitiesPositions);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local BillOfQuantitiesPositions: ',
        error?.message
      );
      throw error;
    }
  }

  async initializeCompleted(): Promise<void> {

  }

}
